/**
 * 协议查看器组件
 * 用于展示协议内容，支持 Markdown 渲染
 */

import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { zhCN, enUS, ja } from "date-fns/locale";
import type { Agreement } from "@/types/consent";

interface AgreementViewerProps {
  /** 协议数据 */
  agreement: Agreement;
  /** 是否显示变更日志 */
  showChangelog?: boolean;
}

/**
 * 获取 date-fns locale
 */
function getDateLocale(lang: string) {
  switch (lang) {
    case "zh":
      return zhCN;
    case "jp":
      return ja;
    default:
      return enUS;
  }
}

/**
 * 协议查看器
 *
 * 功能：
 * - 使用 ReactMarkdown 渲染协议内容
 * - 显示版本号和更新日期
 * - 可选显示变更日志
 */
export default function AgreementViewer({
  agreement,
  showChangelog = true,
}: AgreementViewerProps) {
  const { t, i18n } = useTranslation("consent");
  const dateLocale = getDateLocale(i18n.language);

  // 判断内容是否为 HTML
  const isHtmlContent = (content: string): boolean => {
    return /<[a-z][\s\S]*>/i.test(content);
  };

  return (
    <div className="h-full flex flex-col">
      {/* 版本信息 */}
      <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
        <Badge variant="outline">v{agreement.version}</Badge>
        {agreement.updated_at && (
          <span>
            {t("agreement.updatedAt")}{" "}
            {format(new Date(agreement.updated_at), "PPP", {
              locale: dateLocale,
            })}
          </span>
        )}
      </div>

      {/* 变更日志 */}
      {showChangelog && agreement.changelog && (
        <div className="mb-3 p-3 bg-muted rounded-lg">
          <p className="text-sm font-medium mb-1">
            {t("agreement.changelog")}：
          </p>
          <p className="text-sm text-muted-foreground">{agreement.changelog}</p>
        </div>
      )}

      {/* 协议内容 */}
      <ScrollArea className="flex-1 border rounded-lg p-4 h-[300px]">
        <div className="prose prose-sm max-w-none dark:prose-invert">
          {isHtmlContent(agreement.content) ? (
            <div dangerouslySetInnerHTML={{ __html: agreement.content }} />
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {agreement.content}
            </ReactMarkdown>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
